import styled from 'styled-components';
import { fadeUp, popIn, FACE_ANIMATIONS } from './animations';

export const FixedWrapper = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

export const Wrapper = styled.div`
  max-width: 56rem;
  background: #fff;
  margin: 0 auto;
  padding: 30px;
  margin-top: 20px;
  border-radius: 20px;
  transform: scale(1);

  @media (min-width: 480px) {
    transform: scale(1.2);
  }
  @media (min-width: 768px) {
    transform: scale(1.4);
  }
  @media (min-width: 1024px) {
    transform: scale(1.55);
  }
  @media (min-width: 1280px) {
    transform: scale(1.7);
  }
`;

export const HeaderBlock = styled.div`
  margin-bottom: 3rem;
`;

export const SectionLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6d28d9;
  margin-bottom: 0.5rem;
`;

export const Headline = styled.h1`
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  line-height: 1.1;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem;
`;

export const SubHeadline = styled.p`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

export const ModuleName = styled.span`
  font-weight: 600;
  color: var(--primary, #2563eb);
`;

export const OuterCard = styled.div`
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 1.5rem;
`;

export const InnerCard = styled.div`
  background: white;
  border-radius: 1.25rem;
  padding: 2.5rem;
  box-shadow: 0 32px 32px rgba(25, 28, 30, 0.04);

  @media (max-width: 640px) {
    padding: 1.5rem;
    border-radius: 1rem;
  }
`;

export const RatingGroupTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1.5rem;
  text-align: center;
`;

export const EmojiRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  background: #f1f5f9;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  gap: 0.5rem;
  margin: auto;
`;

export const EmojiButton = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  background: ${({ $selected }) => ($selected ? 'rgba(37, 99, 235, 0.10)' : 'transparent')};
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  min-height: 5.5rem; /* 44px touch target */
  padding: 0.75rem 0.5rem;
  color: ${({ $selected }) => ($selected ? 'var(--primary, #2563EB)' : '#94A3B8')};
  opacity: ${({ $selected, $hasSelection }) => ($selected ? 1 : $hasSelection ? 0.45 : 0.75)};
  transition:
    opacity 200ms ease-out,
    transform 180ms ease-out,
    color 200ms ease-out,
    background 200ms ease-out;

  &:hover {
    opacity: 1;
    color: var(--primary, #2563eb);
    background: rgba(37, 99, 235, 0.1);
    transform: translateY(-3px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary, #2563eb);
    outline-offset: 3px;
    opacity: 1;
    color: var(--primary, #2563eb);
  }

  @media (prefers-reduced-motion: reduce) {
    transition:
      opacity 200ms,
      color 200ms,
      background 200ms;
    &:hover {
      transform: none;
    }
  }
`;

export const FaceWrap = styled.div`
  ${({ $selected, $animKey }) => $selected && FACE_ANIMATIONS[$animKey]}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const EmojiLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $selected }) => ($selected ? 'var(--primary, #2563EB)' : '#64748B')};
  transition: color 200ms;
  text-align: center;
  line-height: 1.2;
`;

export const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

export const SubmitBtn = styled.button`
  padding: 0.875rem 2.5rem;
  background: linear-gradient(135deg, var(--primary, #2563eb), #3b82f6);
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.2);
  transition:
    transform 200ms ease-out,
    box-shadow 200ms ease-out;
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    transform: scale(1.04);
    box-shadow: 0 12px 28px rgba(37, 99, 235, 0.28);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid var(--primary, #2563eb);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

export const SuccessWrap = styled.div`
  text-align: center;
  padding: 3rem;
  animation: ${fadeUp} 350ms ease-out;
`;

export const CheckCircle = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  animation: ${popIn} 400ms ease-out;
`;

export const SuccessTitle = styled.p`
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem;
`;

export const SuccessSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
`;
